import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { User, Trophy, Target, Shield, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * FlyerImage — renders a team-selection flyer with transparent hotspot
 * overlays positioned over each player's name. Tapping a name opens
 * the PlayerProfileCard dialog.
 */
export function FlyerImage({ item, imgRef, onLoad, className, style }) {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const wrapRef = useRef(null);

  const hotspots = item?.player_hotspots || [];
  const hasHotspots = hotspots.length > 0;

  const handleLoad = (e) => {
    setReady(true);
    onLoad?.(e);
  };

  const handlePlayerClick = (e, hotspot) => {
    e.stopPropagation();
    setSelectedPlayer(hotspot.name);
    setProfileOpen(true);
  };

  // Use a wrapper that collapses to the exact rendered image size.
  // The img is display:block with max-w/max-h constraints, and the
  // wrapper is inline-block so it shrink-wraps around it.
  // Hotspots use percentage positioning (left/top/width/height in %)
  // which maps directly to the displayed image dimensions.
  return (
    <>
      <div className="flex justify-center" style={style}>
        <div ref={wrapRef} className="relative inline-block">
          <img
            ref={imgRef}
            onLoad={handleLoad}
            src={item.image_url}
            alt={item.title}
            className="block max-w-full max-h-[60dvh] h-auto rounded-lg"
            data-testid="news-article-image"
          />
          {hasHotspots && ready && hotspots.map((hs, i) => (
            <button
              key={i}
              data-testid={`player-hotspot-${i}`}
              className="absolute cursor-pointer hover:bg-white/15 active:bg-white/25 transition-colors duration-150 rounded-sm"
              style={{
                left: `${hs.x_pct}%`,
                top: `${hs.y_pct}%`,
                width: `${hs.w_pct}%`,
                height: `${hs.h_pct}%`,
              }}
              onClick={(e) => handlePlayerClick(e, hs)}
              title={hs.name}
              aria-label={`View ${hs.name} stats`}
            />
          ))}
        </div>
      </div>
      <PlayerProfileCard
        playerName={selectedPlayer}
        open={profileOpen}
        onClose={() => { setProfileOpen(false); setSelectedPlayer(null); }}
      />
    </>
  );
}


/**
 * PlayerProfileCard — modal showing a player's profile photo and
 * current-season stats (batting, bowling, fielding, match count).
 */
export function PlayerProfileCard({ playerName, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !playerName) return;
    setLoading(true);
    setError(null);
    setData(null);
    api.get(`/public/player-card/${encodeURIComponent(playerName)}`)
      .then((res) => {
        setData(res.data);
      })
      .catch((err) => {
        console.error("Player card fetch error:", err);
        setError("Could not load player stats");
      })
      .finally(() => setLoading(false));
  }, [open, playerName]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="p-0 w-[92vw] max-w-md rounded-2xl border-0 shadow-2xl bg-white overflow-hidden"
        data-testid="player-profile-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{playerName || "Player Profile"}</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16" data-testid="player-card-loading">
            <Loader2 className="h-8 w-8 animate-spin text-[#003366]" />
          </div>
        )}

        {error && (
          <div className="p-6 text-center text-red-600" data-testid="player-card-error">
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <div className="flex flex-col" data-testid="player-card-content">
            {/* Header with avatar + name */}
            <div className="bg-[#003366] px-5 py-5 flex items-center gap-4">
              {data.avatar_url ? (
                <img
                  src={data.avatar_url}
                  alt={data.player_name}
                  className="w-16 h-16 rounded-full border-2 border-white/40 object-cover"
                  data-testid="player-avatar"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-white/10 border-2 border-white/20 flex items-center justify-center">
                  <User className="h-8 w-8 text-white/60" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-white truncate" data-testid="player-card-name">
                  {data.player_name || playerName}
                </h2>
                <p className="text-sm text-white/70">{data.season || "Current Season"}</p>
                {data.found && data.matches > 0 && (
                  <Badge className="mt-1 bg-white/15 text-white border-white/20 text-xs">
                    {data.matches} match{data.matches !== 1 ? "es" : ""}
                  </Badge>
                )}
              </div>
            </div>

            {/* Stats body */}
            {data.found && data.matches > 0 ? (
              <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
                {/* Batting */}
                {data.batting && data.batting.innings > 0 && (
                  <StatSection
                    icon={<Trophy className="h-4 w-4" />}
                    title="Batting"
                    color="text-amber-700"
                    bgColor="bg-amber-50"
                    stats={[
                      { label: "Inn", value: data.batting.innings },
                      { label: "Runs", value: data.batting.runs },
                      { label: "Avg", value: data.batting.average ?? "-" },
                      { label: "HS", value: data.batting.high_score },
                      { label: "NO", value: data.batting.not_outs },
                      { label: "SR", value: data.batting.strike_rate ?? "-" },
                      { label: "50s", value: data.batting.fifties },
                      { label: "100s", value: data.batting.hundreds },
                    ]}
                  />
                )}

                {/* Bowling */}
                {data.bowling && data.bowling.innings > 0 && (
                  <StatSection
                    icon={<Target className="h-4 w-4" />}
                    title="Bowling"
                    color="text-blue-700"
                    bgColor="bg-blue-50"
                    stats={[
                      { label: "Inn", value: data.bowling.innings },
                      { label: "Overs", value: data.bowling.overs },
                      { label: "Wkts", value: data.bowling.wickets },
                      { label: "Avg", value: data.bowling.average ?? "-" },
                      { label: "Econ", value: data.bowling.economy ?? "-" },
                      { label: "Best", value: data.bowling.best_figures ?? "-" },
                    ]}
                  />
                )}

                {/* Fielding */}
                {data.fielding && (data.fielding.catches > 0 || data.fielding.run_outs > 0 || data.fielding.stumpings > 0) && (
                  <StatSection
                    icon={<Shield className="h-4 w-4" />}
                    title="Fielding"
                    color="text-emerald-700"
                    bgColor="bg-emerald-50"
                    stats={[
                      { label: "Catches", value: data.fielding.catches },
                      { label: "Run Outs", value: data.fielding.run_outs },
                      { label: "Stumpings", value: data.fielding.stumpings },
                    ]}
                  />
                )}

                {/* If no batting/bowling/fielding at all */}
                {(!data.batting || data.batting.innings === 0) &&
                  (!data.bowling || data.bowling.innings === 0) &&
                  (!data.fielding || (data.fielding.catches === 0 && data.fielding.run_outs === 0 && data.fielding.stumpings === 0)) && (
                  <p className="text-center text-muted-foreground text-sm py-4">
                    No detailed stats recorded yet this season
                  </p>
                )}
              </div>
            ) : (
              <div className="p-6 text-center text-muted-foreground" data-testid="player-card-no-stats">
                <p className="text-sm">No stats available for this season</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function StatSection({ icon, title, color, bgColor, stats }) {
  return (
    <div className={`rounded-xl ${bgColor} p-3`} data-testid={`stat-section-${title.toLowerCase()}`}>
      <div className={`flex items-center gap-2 mb-2 ${color} font-semibold text-sm`}>
        {icon}
        {title}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {stats.map((s) => (
          <div key={s.label} className="text-center">
            <div className="text-lg font-bold text-foreground leading-tight">{s.value}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
